import React, { useEffect } from 'react';
import { Plus } from 'phosphor-react';
import { calculateSetsWon } from '../utils/matchCompletion';
import { useToast } from '../contexts/ToastContext';
import './Sets.css';

function Sets({
  currentSet = 1,
  setCurrentSet,
  teamAScore = 0,
  teamBScore = 0,
  setTeamAScore,
  setTeamBScore,
  setsWon = { teamA: 0, teamB: 0 },
  setSetsWon,
  setAllSetScores,
  allSetScores = [],
  hideIcon = false,
  readOnly = false,
  viewerSet,
  setViewerSet,
  setMatchState,
  manualViewerSet,
  setManualViewerSet,
  onSetSwitch,
}) {
  const toast = useToast();
  const isViewer = readOnly || !setAllSetScores || !setCurrentSet;

  useEffect(() => {
    if (!isViewer && allSetScores.length === 0 && setAllSetScores) {
      setAllSetScores([{ teamA: 0, teamB: 0 }]);
    }
  }, [isViewer, allSetScores, setAllSetScores]);

  useEffect(() => {
    const isEditingPastSet = currentSet <= allSetScores.length;

    if (
      !isViewer &&
      isEditingPastSet &&
      allSetScores.length > 0 &&
      setAllSetScores
    ) {
      const existingScore = allSetScores[currentSet - 1];
      const newScore = { teamA: teamAScore, teamB: teamBScore };

      const isDifferent =
        existingScore?.teamA !== newScore.teamA ||
        existingScore?.teamB !== newScore.teamB;

      if (isDifferent) {
        const updatedScores = [...allSetScores];
        updatedScores[currentSet - 1] = newScore;
        setAllSetScores(updatedScores);
      }
    }
  }, [teamAScore, teamBScore, currentSet, allSetScores, isViewer, setAllSetScores]);

  const handleAddSet = () => {
    // Check if we've reached maximum sets
    if (allSetScores.length >= 5) {
      toast.warning("Maximum of 5 sets allowed in volleyball");
      return;
    }

    if (!window.confirm("Are you sure you want to create a new set?")) {
      return;
    }

    const updatedScores = [...allSetScores];
    updatedScores[currentSet - 1] = { teamA: teamAScore, teamB: teamBScore };
    updatedScores.push({ teamA: 0, teamB: 0 });
    setAllSetScores(updatedScores);

    if (setSetsWon) {
      const computed = calculateSetsWon(updatedScores);
      setSetsWon(computed);
    }

    // Notify parent that we're switching sets (for animation control)
    if (onSetSwitch) {
      onSetSwitch(true);
    }

    setTeamAScore(0);
    setTeamBScore(0);
    setCurrentSet(currentSet + 1);
    
    // Reset the flag after scores are updated
    if (onSetSwitch) {
      setTimeout(() => onSetSwitch(false), 100);
    }
  };

  const handleSetClick = (index) => {
    if (isViewer && setViewerSet && setManualViewerSet) {
      setManualViewerSet(true);
      setViewerSet(index + 1);
      return;
    }

    if (!isViewer && setCurrentSet && setTeamAScore && setTeamBScore) {
      const selectedSet = allSetScores[index];
      
      // Notify parent that we're switching sets (for animation control)
      if (onSetSwitch) {
        onSetSwitch(true);
      }
      
      setCurrentSet(index + 1);
      setTeamAScore(selectedSet.teamA);
      setTeamBScore(selectedSet.teamB);
      
      // Reset the flag after scores are updated
      if (onSetSwitch) {
        setTimeout(() => onSetSwitch(false), 100);
      }
    }
  };

  return (
    <div className="sets-bar">
      {(allSetScores || []).map((score, index) => {
        const setNum = index + 1;
        const isActive = isViewer
          ? viewerSet === setNum
          : currentSet === setNum;
        return (
          <div
            key={index}
            className={`set-bubble ${isActive ? 'active' : ''}`}
            onClick={() => handleSetClick(index)}
          >
            <div className="set-number">{setNum}</div>
            <div className="set-scores">
              <div className="score-top">{score.teamA}</div>
              <div className="score-bottom">{score.teamB}</div>
            </div>
          </div>
        );
      })}
      {!hideIcon && !isViewer && (
        <button className="add-set-btn" onClick={handleAddSet} title="Add Set">
          <Plus size={20} weight="fill" color="#000000" />
        </button>
      )}
    </div>
  );
}

export default Sets;
